// Coloured headings (ADR-0060, block 5b): the full stop of a short title and the key words of a long one.
// The accent is decorative: the title text itself stays one phrase, in the DOM, the tab title and the SEO tags.
import type { ReactNode } from "react";

/** The colour of a section's full stop (owner 2026-10-14): each section keeps its own, echoing the home title. */
const DOT = { green: "hl-g", magenta: "hl-1", sky: "hl-2", tangerine: "hl-3" } as const;

/** A short section title in its colour with the full stop ("Connections."). The dot is not read aloud. */
export function Title({ text, dot = "green" }: { text: string; dot?: keyof typeof DOT }) {
  const cls = DOT[dot];
  // The whole title wears the section colour (owner 2026-10-14), as the active pill in the navigation; the full stop too.
  const words = text.endsWith(".") ? text.slice(0, -1) : text;
  return (
    <>
      <span className={cls}>{words}</span>
      {text.endsWith(".") ? <span className={cls} aria-hidden="true">.</span> : <span className={`${cls} hl-dot`} aria-hidden="true">.</span>}
    </>
  );
}

/** Wraps the first occurrence of each phrase in the given accent class; the rest stays plain text. */
export function Marked({ text, marks }: { text: string; marks: ReadonlyArray<readonly [string, string]> }) {
  const found = marks
    .map(([phrase, cls]) => ({ at: text.indexOf(phrase), phrase, cls }))
    .filter((m) => m.at >= 0)
    .sort((a, b) => a.at - b.at);
  const out: ReactNode[] = [];
  let pos = 0;
  for (const m of found) {
    if (m.at < pos) continue;
    if (m.at > pos) out.push(text.slice(pos, m.at));
    out.push(
      <span key={m.at} className={m.cls}>
        {m.phrase}
      </span>,
    );
    pos = m.at + m.phrase.length;
  }
  out.push(text.slice(pos));
  return <>{out}</>;
}

/** Home: "Your marketing numbers, answered in plain words" → magenta, sky, tangerine. */
export function HomeTitle({ text }: { text: string }) {
  return (
    <Marked
      text={text}
      marks={[
        ["numbers", "hl-1"],
        ["answered", "hl-2"],
        ["plain words", "hl-3"],
      ]}
    />
  );
}

/** Long landing titles: one key word in magenta ("MCP", else the product name). */
export function KeywordTitle({ text }: { text: string }) {
  const phrase = text.includes("MCP") ? "MCP" : "V-Kit";
  return <Marked text={text} marks={[[phrase, "hl-1"]]} />;
}
